// Venue equipment inventory taxonomy for the rider-check editor.
//
// Categories mirror the snake_case keys used in the venue_equipment collection
// (and rider_check.py's CATEGORY_LABELS) so the inventory the venue enters here
// feeds the rider compatibility diff later. Additive: nothing here renames or
// touches the rider side.

// Canonical category list shown in the editor's category Select.
export const INVENTORY_CATEGORIES = [
  { value: 'foh_console', label: 'FOH console' },
  { value: 'foh_input_count', label: 'FOH channels' },
  { value: 'foh_snake', label: 'FOH snake' },
  { value: 'monitor_wedge', label: 'Monitor wedges' },
  { value: 'pa_main', label: 'PA mains' },
  { value: 'pa_sub', label: 'PA subs' },
  { value: 'di_active', label: 'DI active' },
  { value: 'di_passive', label: 'DI passive' },
  { value: 'di_stereo', label: 'DI stereo' },
  { value: 'bass_amp', label: 'Bass amp' },
  { value: 'guitar_amp', label: 'Guitar amp' },
  { value: 'drum_kit', label: 'Drum kit' },
  { value: 'cymbals', label: 'Cymbals' },
  { value: 'mic_dyn_vocal', label: 'Dynamic vocal mics' },
  { value: 'mic_dyn_instr', label: 'Dynamic instrument mics' },
  { value: 'mic_kick', label: 'Kick mic' },
  { value: 'mic_condenser', label: 'Condenser mics' },
  { value: 'mic_wireless', label: 'Wireless mics' },
  { value: 'iem', label: 'IEM systems' },
  { value: 'keys_amp', label: 'Keys amp' },
];

// Capacity categories store a single numeric total in `attributes` (e.g. the
// number of available FOH channels) rather than being counted as discrete units.
// Keep this list in sync with the backend's INVENTORY_CAPACITY_CATEGORIES.
export const CAPACITY_CATEGORIES = ['foh_input_count'];

// category -> match_type map (mirrors the server-side derivation; the server is
// authoritative — this drives the editor UI only).
export const CATEGORY_MATCH_TYPE = INVENTORY_CATEGORIES.reduce((acc, c) => {
  acc[c.value] = CAPACITY_CATEGORIES.includes(c.value) ? 'capacity' : 'discrete';
  return acc;
}, {});

export const isCapacityCategory = (category) => CAPACITY_CATEGORIES.includes(category);

// For each capacity category, the attribute key + label used by the single
// numeric "capacity/total" field in the editor.
export const CAPACITY_FIELD = {
  foh_input_count: { key: 'channels', label: 'Channels available' },
};

export const capacityFieldFor = (category) =>
  CAPACITY_FIELD[category] || { key: 'capacity', label: 'Capacity' };

export const labelForCategory = (category) =>
  (INVENTORY_CATEGORIES.find((c) => c.value === category) || {}).label || category;

// The instrument-side gear collectively called "backline" — reserved for
// amps, drum kit and cymbals. Single source of truth for both the plain
// grouped select (musician rider builder, MyRiderPage.jsx) and the venue
// category combobox's BACKLINE group below.
export const BACKLINE_CATEGORY_KEYS = ['bass_amp', 'guitar_amp', 'keys_amp', 'drum_kit', 'cymbals'];

// Splits a { value, label } categories array — either INVENTORY_CATEGORIES or
// the backend /rider-check/taxonomy response, same shape — into a "Backline"
// group and an "Other equipment" group for a grouped Select, preserving the
// relative order within each group. Used by the musician rider builder
// (MyRiderPage.jsx); the venue inventory editor uses the richer
// CategoryCombobox / INVENTORY_GROUPS below instead.
export const groupCategoriesForSelect = (categories) => {
  const backline = [];
  const other = [];
  (categories || []).forEach((c) => {
    (BACKLINE_CATEGORY_KEYS.includes(c.value) ? backline : other).push(c);
  });
  return [
    { label: 'Backline', options: backline },
    { label: 'Other equipment', options: other },
  ].filter((g) => g.options.length > 0);
};

// Grouping for the category combobox. Explicit lists first, in display
// order; any category not named above falls into OTHER automatically so a
// future addition to INVENTORY_CATEGORIES never silently disappears from
// the picker.
const EXPLICIT_CATEGORY_GROUPS = [
  { header: 'MIXING', categories: ['foh_console', 'foh_input_count', 'foh_snake'] },
  { header: 'MONITORING', categories: ['monitor_wedge', 'iem'] },
  { header: 'PA', categories: ['pa_main', 'pa_sub'] },
  { header: 'MICROPHONES', categories: ['mic_dyn_vocal', 'mic_dyn_instr', 'mic_kick', 'mic_condenser', 'mic_wireless'] },
  { header: 'DI', categories: ['di_active', 'di_passive', 'di_stereo'] },
  { header: 'BACKLINE', categories: BACKLINE_CATEGORY_KEYS },
  { header: 'STANDS & CABLING', categories: [] },
];

const explicitlyGrouped = new Set(EXPLICIT_CATEGORY_GROUPS.flatMap((g) => g.categories));
const otherCategoryValues = INVENTORY_CATEGORIES
  .map((c) => c.value)
  .filter((v) => !explicitlyGrouped.has(v));

export const INVENTORY_GROUPS = [
  ...EXPLICIT_CATEGORY_GROUPS,
  { header: 'OTHER', categories: otherCategoryValues },
].map((group) => ({
  header: group.header,
  categories: group.categories
    .map((value) => INVENTORY_CATEGORIES.find((c) => c.value === value))
    .filter(Boolean),
}));

// Search aliases so typing the gear name (not just the category label) finds
// a category — e.g. "sm58" -> Dynamic vocal mics. Keyed by category value.
export const CATEGORY_ALIASES = {
  foh_console: ['desk', 'mixer', 'console'],
  monitor_wedge: ['wedge', 'floor monitor'],
  mic_dyn_vocal: ['sm58', 'vocal'],
  mic_dyn_instr: ['sm57'],
  di_active: ['di', 'direct'],
  di_passive: ['di', 'direct'],
  di_stereo: ['di', 'direct'],
  drum_kit: ['kit', 'drums'],
  bass_amp: ['amp'],
  guitar_amp: ['amp'],
  keys_amp: ['amp'],
};

export const aliasesForCategory = (category) => CATEGORY_ALIASES[category] || [];

// Category-specific "model" field placeholders. Falls back to the generic
// example when a category has none, or none is selected yet.
const DEFAULT_MODEL_PLACEHOLDER = 'e.g. Shure SM58';

const MODEL_PLACEHOLDERS = {
  foh_console: 'e.g. Allen & Heath SQ6',
  foh_input_count: 'e.g. Behringer S16 stagebox',
  foh_snake: 'e.g. Whirlwind M-24/4',
  monitor_wedge: 'e.g. Martin Audio LE350',
  pa_main: 'e.g. d&b Y8',
  pa_sub: 'e.g. d&b Y-SUB',
  di_active: 'e.g. BSS AR-133',
  di_passive: 'e.g. Radial ProDI',
  di_stereo: 'e.g. Radial ProD2',
  bass_amp: 'e.g. Ampeg SVT-4PRO',
  guitar_amp: 'e.g. Fender Twin Reverb',
  drum_kit: 'e.g. Tama Superstar',
  cymbals: 'e.g. Zildjian A Custom set',
  mic_dyn_vocal: 'e.g. Shure SM58',
  mic_dyn_instr: 'e.g. Shure SM57',
  mic_kick: 'e.g. Shure Beta 52A',
  mic_condenser: 'e.g. AKG C414',
  mic_wireless: 'e.g. Shure SLXD24',
  iem: 'e.g. Shure PSM300',
  keys_amp: 'e.g. Roland KC-500',
};

export const modelPlaceholderFor = (category) =>
  MODEL_PLACEHOLDERS[category] || DEFAULT_MODEL_PLACEHOLDER;

// Quick-add rows so the editor is never blank. Category and a starting count
// only — `model` is deliberately empty for the venue to fill in with what it
// actually owns. Never name a brand or model here: inventory is user-supplied,
// and a pre-filled model is a guess the venue can accept without reading.
// The quick-add buttons render labelForCategory(), not the model.
export const INVENTORY_TEMPLATES = [
  { category: 'foh_console', model: '', quantity: 1 },
  { category: 'foh_input_count', model: '', quantity: 1, attributes: { channels: 32 } },
  { category: 'monitor_wedge', model: '', quantity: 4 },
  { category: 'di_active', model: '', quantity: 2 },
  { category: 'mic_dyn_vocal', model: '', quantity: 4 },
  { category: 'mic_dyn_instr', model: '', quantity: 4 },
  { category: 'drum_kit', model: '', quantity: 1 },
];
